import { parseDate } from "@internationalized/date";
import { t } from "@travily/i18n";
import { ButtonDock } from "@ui/components/ButtonDock/ButtonDock";
import { Dialog } from "@ui/components/Dialog";
import { Field, useField } from "@ui/components/Field";
import { Icon } from "@ui/components/Icon/Icon";
import { StandardAutocomplete } from "@ui/components/StandardAutocomplete";
import { StandardButton } from "@ui/components/StandardButton/StandardButton";
import { StandardDateRangePicker } from "@ui/components/StandardDateRangePicker";
import { StandardDropZone } from "@ui/components/StandardDropZone";
import { StandardTextArea } from "@ui/components/StandardTextArea";
import type { DateValue, RangeValue } from "@ui/primitives/shared";
import { useCallback, useMemo } from "react";
import { FamilyToggleGroup } from "./FamilyToggleGroup";
import { FamilyToggleItem } from "./FamilyToggleItem";
import type { TripModalProps } from "./types";

export type { TripModalProps } from "./types";

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

interface MembersFieldProps {
  members: TripModalProps["members"];
  selectedMemberKeys: TripModalProps["selectedMemberKeys"];
  onSelectedMembersChange: TripModalProps["onSelectedMembersChange"];
  onAddPerson?: TripModalProps["onAddPerson"];
  hasError: boolean;
}

function MemberTile({ member }: { member: TripModalProps["members"][number] }) {
  return (
    <FamilyToggleItem
      avatar={member.avatar}
      color={member.color}
      id={String(member.id)}
      initial={member.initial}
      label={member.name ?? member.initial}
    />
  );
}

/** The "who visited" picker. Reads the surrounding Field's ids so the (non-RAC-field)
 *  toggle group is labelled by the visible Field.Label and described by Field.Error.
 *  Household first, then a full-width rule, then guests — the tile grid cannot
 *  carry the stacked section headings the Who list uses. Add is last. */
function MembersField({
  members,
  selectedMemberKeys,
  onSelectedMembersChange,
  onAddPerson,
  hasError,
}: MembersFieldProps) {
  const fieldCtx = useField();
  const household = members.filter((member) => !member.guest);
  const guests = members.filter((member) => member.guest);

  return (
    <FamilyToggleGroup
      aria-describedby={hasError ? fieldCtx?.errorId : undefined}
      aria-labelledby={fieldCtx?.labelId}
      onSelectionChange={onSelectedMembersChange}
      selectedKeys={selectedMemberKeys}
    >
      {household.map((member) => (
        <MemberTile key={member.id} member={member} />
      ))}
      {/* A full-width rule, so the split survives wrapping: a vertical hairline
          only reads as a divider while every tile sits on one row. */}
      {guests.length > 0 && <span aria-hidden="true" className="col-span-full h-px w-full bg-neutral-200" />}
      {guests.map((member) => (
        <MemberTile key={member.id} member={member} />
      ))}
      {onAddPerson && (
        <button
          className="flex w-full max-w-[76px] cursor-pointer flex-col items-center justify-self-center gap-2 rounded-lg bg-transparent p-0 outline-none focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2"
          onClick={onAddPerson}
          type="button"
        >
          <span className="flex size-[60px] items-center justify-center rounded-full bg-neutral-100 text-neutral-700">
            <Icon name="plus" />
          </span>
          <span className="text-center text-neutral-900 text-sm leading-5 md:text-base md:leading-6">
            {t("components.views.trips.trip_modal.add_person")}
          </span>
        </button>
      )}
    </FamilyToggleGroup>
  );
}

export function TripModal({
  mode,
  close,
  locationSearch,
  locationError,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  dateError,
  description,
  onDescriptionChange,
  descriptionError,
  members,
  selectedMemberKeys,
  onSelectedMembersChange,
  onAddPerson,
  membersError,
  photoPreviewSrc,
  onPhotoFileSelect,
  onPhotoRemove,
  onSubmit,
  isPending,
}: TripModalProps) {
  const isCreate = mode === "create";

  const dateValue = useMemo<RangeValue<DateValue> | null>(() => {
    if (!startDate || !endDate) return null;
    return { start: parseDate(startDate), end: parseDate(endDate) };
  }, [startDate, endDate]);

  const handleDateChange = useCallback(
    (value: RangeValue<DateValue> | null) => {
      onStartDateChange(value?.start.toString() ?? null);
      onEndDateChange(value?.end.toString() ?? null);
    },
    [onStartDateChange, onEndDateChange],
  );

  return (
    <>
      <Dialog.Header
        onClose={close}
        title={
          isCreate ? t("components.views.trips.trip_modal.add_trip") : t("components.views.trips.trip_modal.edit_trip")
        }
      />
      <Dialog.Body>
        <div className="flex flex-col gap-4 px-1">
          <StandardAutocomplete
            {...locationSearch}
            errorMessage={locationError}
            isInvalid={!!locationError}
            label={t("components.views.trips.trip_modal.destination_label")}
            leadIcon={<Icon name="map" type="sticker" />}
            placeholder={t("components.views.trips.trip_modal.destination_placeholder")}
          />
          <StandardDateRangePicker
            endPlaceholder={t("components.views.trips.trip_modal.end_date")}
            errorMessage={dateError}
            isInvalid={!!dateError}
            label={t("components.views.trips.trip_modal.dates_label")}
            leadIcon={<Icon name="date" type="sticker" />}
            onChange={handleDateChange}
            startPlaceholder={t("components.views.trips.trip_modal.arrival_date")}
            value={dateValue}
          />
          <StandardTextArea
            errorMessage={descriptionError}
            isInvalid={!!descriptionError}
            isOptional
            label={t("components.views.trips.trip_modal.description_label")}
            leadIcon={<Icon name="message" type="sticker" />}
            onChange={onDescriptionChange}
            placeholder={t("components.views.trips.trip_modal.description_placeholder")}
            value={description}
          />
          {members.length > 0 && (
            <Field isInvalid={!!membersError}>
              <Field.Label>{t("components.views.trips.trip_modal.who_visited")}</Field.Label>
              <MembersField
                hasError={!!membersError}
                members={members}
                onAddPerson={onAddPerson}
                onSelectedMembersChange={onSelectedMembersChange}
                selectedMemberKeys={selectedMemberKeys}
              />
              {membersError && <Field.Error>{membersError}</Field.Error>}
            </Field>
          )}
          <Field isOptional>
            <Field.Label>{t("components.views.trips.trip_modal.upload_photo")}</Field.Label>
            <StandardDropZone
              aria-label={t("components.views.trips.trip_modal.upload_photo")}
              acceptedFileTypes={ACCEPTED_IMAGE_TYPES}
              className="h-50 w-full"
              onFileSelect={onPhotoFileSelect}
              onRemove={onPhotoRemove}
              previewSrc={photoPreviewSrc}
            />
          </Field>
        </div>
      </Dialog.Body>
      <Dialog.Footer>
        {/* No cancel: the header's close control already backs out, and a second
            full-width pill cost ~180px of a phone viewport to say the same thing. */}
        <ButtonDock
          primaryAction={
            <StandardButton color="pink" isPending={isPending} onPress={onSubmit} size="44">
              {isCreate
                ? t("components.views.trips.trip_modal.add_to_logbook")
                : t("components.views.trips.trip_modal.save_changes")}
            </StandardButton>
          }
        />
      </Dialog.Footer>
    </>
  );
}
